<template>
    <glass-panel
        radius="lg"
        class="analytics-system pa-6"
    >
        <div class="d-flex align-center justify-space-between mb-5" style="gap:12px">
            <div>
                <eyebrow primary class="mb-1">
                    Tecnologia
                </eyebrow>
                <h3 class="analytics-system__title">
                    Sistemas e navegadores
                </h3>
            </div>
            <v-icon color="primary" size="22">mdi-devices</v-icon>
        </div>

        <div
            v-if="!hasData"
            class="analytics-system__empty"
        >
            <v-icon size="32" color="primary">mdi-monitor-cellphone</v-icon>
            <p class="mt-3 mb-0 body-sm">Sem dados de dispositivos ainda.</p>
        </div>

        <div
            v-else
            class="analytics-system__grid"
        >
            <div
                v-for="block in blocks"
                :key="block.title"
                class="analytics-system__block"
            >
                <div class="analytics-system__block-title">
                    {{ block.title }}
                </div>

                <div class="analytics-system__chart-wrap">
                    <apex-chart
                        :key="block.chartKey"
                        type="donut"
                        :options="block.options"
                        :series="block.series"
                        height="180"
                    />
                    <div class="analytics-system__center">
                        <div class="analytics-system__center-num">
                            {{ formatNumber(block.total) }}
                        </div>
                        <div class="analytics-system__center-label">
                            visitantes
                        </div>
                    </div>
                </div>

                <ul class="analytics-system__legend">
                    <li
                        v-for="(slice, i) in block.slices"
                        :key="slice.label"
                        class="analytics-system__legend-item"
                    >
                        <span
                            class="analytics-system__legend-dot"
                            :style="{ background: PALETTE[i % PALETTE.length] }"
                        />
                        <span class="analytics-system__legend-name">{{ slice.label }}</span>
                        <span class="analytics-system__legend-pct">{{ slice.pct }}%</span>
                    </li>
                </ul>
            </div>
        </div>
    </glass-panel>
</template>

<script>
const PALETTE = ['#702ae1', '#9170e8', '#6448b2', '#b28cff', '#5f5292', '#c3b4fc']

const MAX_SLICES = 5

const formatNumber = (n) => {
    if (n == null || isNaN(n)) return '—'
    return Number(n).toLocaleString('pt-BR')
}

const normalizeBrowser = (raw) => {
    if (!raw) return 'Outro'
    const lower = String(raw).toLowerCase()
    if (lower.includes('chrome')) return 'Chrome'
    if (lower.includes('safari')) return 'Safari'
    if (lower.includes('firefox')) return 'Firefox'
    if (lower.includes('edge')) return 'Edge'
    if (lower.includes('opera')) return 'Opera'
    if (lower.includes('samsung')) return 'Samsung Internet'
    return raw
}

const normalizeOS = (raw) => {
    if (!raw) return 'Outro'
    const lower = String(raw).toLowerCase()
    if (lower.includes('windows')) return 'Windows'
    if (lower.includes('android')) return 'Android'
    if (lower.includes('ios') || lower.includes('iphone') || lower.includes('ipad')) return 'iOS'
    if (lower.includes('mac')) return 'macOS'
    if (lower.includes('linux')) return 'Linux'
    return raw
}

const aggregate = (rows, getKey) => {
    const buckets = new Map()
    for (const item of rows || []) {
        const key = getKey(item)
        if (!key) continue
        const visitors = Number(item.data?.VISITORS) || 0
        buckets.set(key, (buckets.get(key) || 0) + visitors)
    }
    const sorted = Array.from(buckets.entries())
        .map(([label, visitors]) => ({ label, visitors }))
        .filter(b => b.visitors > 0)
        .sort((a, b) => b.visitors - a.visitors)

    if (sorted.length <= MAX_SLICES) return sorted
    const top = sorted.slice(0, MAX_SLICES - 1)
    const rest = sorted.slice(MAX_SLICES - 1).reduce((sum, r) => sum + r.visitors, 0)
    if (rest > 0) top.push({ label: 'Outros', visitors: rest })
    return top
}

const buildDonutOptions = (labels) => ({
    chart: {
        type: 'donut',
        fontFamily: 'Manrope, sans-serif',
        background: 'transparent',
        animations: { enabled: true, easing: 'easeinout', speed: 500 },
    },
    labels,
    colors: PALETTE,
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    plotOptions: {
        pie: {
            donut: {
                size: '70%',
                labels: { show: false },
            },
            expandOnClick: false,
        },
    },
    tooltip: {
        y: {
            formatter: (val) => `${Number(val).toLocaleString('pt-BR')} visitantes`,
        },
    },
    states: {
        hover: { filter: { type: 'lighten', value: 0.05 } },
    },
})

export default {
    name: 'AnalyticsSystem',
    props: {
        system: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return { PALETTE }
    },
    methods: { formatNumber },
    computed: {
        hasData() {
            return Array.isArray(this.system) && this.system.length > 0
        },
        osSlices() {
            return aggregate(this.system, item => normalizeOS(item?.dimension?.os))
        },
        browserSlices() {
            return aggregate(this.system, item => normalizeBrowser(item?.dimension?.browser))
        },
        blocks() {
            const buildBlock = (title, slices) => {
                const total = slices.reduce((sum, s) => sum + s.visitors, 0)
                const series = slices.map(s => s.visitors)
                const labels = slices.map(s => s.label)
                const slicesWithPct = slices.map(s => ({
                    ...s,
                    pct: total > 0 ? Math.round((s.visitors / total) * 100) : 0,
                }))
                return {
                    title,
                    series,
                    slices: slicesWithPct,
                    total,
                    options: buildDonutOptions(labels),
                    chartKey: `${title}-${labels.join('-')}`,
                }
            }
            return [
                buildBlock('Sistema operacional', this.osSlices),
                buildBlock('Navegador', this.browserSlices),
            ].filter(b => b.series.length > 0)
        },
    },
}
</script>

<style scoped>
.analytics-system__title {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.25rem;
    letter-spacing: -0.01em;
    color: var(--color-dark-violet);
    margin: 0;
}

html.theme-dark .analytics-system__title {
    color: var(--color-on-surface);
}

.analytics-system__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}

@media (min-width: 540px) {
    .analytics-system__grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.analytics-system__block {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.analytics-system__block-title {
    font-family: var(--font-body);
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-on-surface-variant);
    margin-bottom: 8px;
}

.analytics-system__chart-wrap {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
}

.analytics-system__center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.analytics-system__center-num {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.25rem;
    line-height: 1;
    color: var(--color-dark-violet);
    letter-spacing: -0.01em;
}

html.theme-dark .analytics-system__center-num {
    color: var(--color-on-surface);
}

.analytics-system__center-label {
    font-family: var(--font-body);
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-on-surface-variant);
    margin-top: 4px;
}

.analytics-system__legend {
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.analytics-system__legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-body);
    font-size: 0.8125rem;
}

.analytics-system__legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 9999px;
    flex-shrink: 0;
}

.analytics-system__legend-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-on-surface);
    font-weight: 600;
}

.analytics-system__legend-pct {
    color: var(--color-on-surface-variant);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.analytics-system__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 32px 16px;
    color: var(--color-on-surface-variant);
}
</style>
